import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthGateContext } from '../providers/AuthGateContext';

const KEY = 'ng.gate.counts';
const TS_KEY = 'ng.gate.ts';
const DECAY_MS = 24 * 60 * 60 * 1000; // 24h

function loadCounts() {
  try {
    const now = Date.now();
    const ts = parseInt(localStorage.getItem(TS_KEY) || '0', 10) || 0;
    if (ts && now - ts > DECAY_MS) {
      localStorage.removeItem(KEY);
      localStorage.setItem(TS_KEY, String(now));
      return {};
    }
    const data = JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    if (!ts) localStorage.setItem(TS_KEY, String(now));
    return data;
  } catch {
    return {};
  }
}

function saveCounts(obj) {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
    localStorage.setItem(TS_KEY, String(Date.now()));
  } catch {}
}

export const useAuthGate = (opts = {}) => {
  const context = useContext(AuthGateContext);
  const limit = opts.limit ?? 2; // allow N soft prompts before hard stop
  const counterScope = opts.scope || 'default';
  const isAuth = context?.isAuth ?? useSelector((s) => s.auth?.isAuthorized);
  const [localJoinOpen, setLocalJoinOpen] = useState(false);
  const [localReason, setLocalReason] = useState(null);
  const joinOpen = context?.joinOpen ?? localJoinOpen;
  const setJoinOpen = context?.setJoinOpen ?? setLocalJoinOpen;
  const reason = context?.reason ?? localReason;
  const setReason = context?.setReason ?? setLocalReason;

  const increment = useCallback((bucket) => {
    const all = loadCounts();
    const scope = all[counterScope] || {};
    scope[bucket] = (scope[bucket] || 0) + 1;
    all[counterScope] = scope;
    saveCounts(all);
    return scope[bucket];
  }, [counterScope]);

  const getCount = useCallback((bucket) => {
    const all = loadCounts();
    const scope = all[counterScope] || {};
    return scope[bucket] || 0;
  }, [counterScope]);

  const pushEvent = () => {};

  const guard = useCallback((bucket = 'interaction', onAuthed) => {
    return (...args) => {
      if (isAuth) return onAuthed?.(...args);
      const seen = getCount(bucket);
      if (seen < limit) {
        const count = increment(bucket);
        // Fire metrics and mark origin for conversion tracking
        pushEvent('auth_gate_shown', { scope: counterScope, bucket, count });
        try { localStorage.setItem('ng.gate.origin', JSON.stringify({ scope: counterScope, bucket })); } catch {}
        setReason(bucket);
        setJoinOpen(true);
      } else {
        pushEvent('auth_gate_limit_hit', { scope: counterScope, bucket });
        setReason(bucket);
        setJoinOpen(true);
      }
    };
  }, [getCount, increment, isAuth, limit, counterScope, setJoinOpen, setReason]);

  return useMemo(
    () => ({ isAuth, joinOpen, setJoinOpen, guard, reason, setReason }),
    [isAuth, joinOpen, guard, setJoinOpen, reason, setReason],
  );
};

export default useAuthGate;
